import React from "react";
import { Chip, styled } from "@mui/material";
import { SxProps, Theme } from "@mui/material/styles";

// Unified badge component for all alignment scores
const AlignmentBadge = styled(Chip)<{ customColor?: string; textColor?: string }>(({ customColor, textColor }) => ({
    fontWeight: 'bold',
    fontSize: '0.875rem',
    minWidth: '60px',
    maxWidth: '100px',
    height: '32px',
    ...(customColor && {
        backgroundColor: customColor,
        color: textColor || '#ffffff',
        '&:hover': {
            backgroundColor: customColor,
            opacity: 0.8,
        },
    }),
}));

// Function to get badge color and variant based on alignment score text
export const getBadgeProps = (alignmentScoreText: string) => {
    switch (alignmentScoreText) {
        case "Unknown":
            return { color: "default" as const, variant: "outlined" as const };
        case "Very low":
            return { 
                customColor: "#d32f2f", 
                textColor: "#ffffff",
                variant: "filled" as const 
            };
        case "Low":
            return { 
                customColor: "#ed6c02", 
                textColor: "#ffffff",
                variant: "filled" as const 
            };
        case "Medium":
            return { 
                customColor: "#fbc02d", 
                textColor: "#000000",
                variant: "filled" as const 
            };
        case "High":
            return { 
                customColor: "#2e7d32", 
                textColor: "#ffffff",
                variant: "filled" as const 
            };
        case "Very high":
            return { 
                customColor: "#388e3c", 
                textColor: "#ffffff",
                variant: "filled" as const 
            };
        case "Perfect":
            return { 
                customColor: "#388e3c", 
                textColor: "#ffffff",
                variant: "filled" as const 
            };
        default:
            return { color: "default" as const, variant: "outlined" as const };
    }
};

interface AlignmentBadgeProps {
    label: string;
    size?: "small" | "medium";
    sx?: SxProps<Theme>;
}

export default function AlignmentBadgeComponent({ label, size = "medium", sx }: AlignmentBadgeProps) {
    return (
        <AlignmentBadge
            label={label}
            {...getBadgeProps(label)}
            size={size}
            sx={sx}
        />
    );
}
